import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { ApiError } from '@/lib/api-client';
import { ContributeIdentifySection } from '@/components/organisms/contribute/contribute-identify-section';
import { ContributeFormSection } from '@/components/organisms/contribute/contribute-form-section';
import { ContributeComposerSection } from '@/components/organisms/contribute/contribute-composer-section';
import { ContributeSettingsSection } from '@/components/organisms/contribute/contribute-settings-section';
import { ContributeSourcesSection } from '@/components/organisms/contribute/contribute-sources-section';
import { ContributeLivePreview } from '@/components/organisms/contribute/contribute-live-preview';
import { DuplicateMatchPanel } from '@/components/molecules/duplicate-match-panel';
import { Button } from '@/components/atoms/button';
import {
  contributeFormReducer,
  contributeFormStorageReplacer,
  initialContributeFormState,
} from '@/lib/contribute-form';
import { buildContributePayload } from '@/lib/contribute-form-payload';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import { useLocalStorageReducer } from '@/lib/hooks/use-local-storage-reducer';
import { useContributePiece, usePieceDuplicates } from '@/lib/queries/pieces';
import { useUploadResourceFile } from '@/lib/queries/resources';
import { useKeys } from '@/lib/queries/reference-data';

const SHEET_MUSIC_RESOURCE_TYPE = 'SHEET_MUSIC';
const AUDIO_RECORDING_RESOURCE_TYPE = 'AUDIO_RECORDING';

const TITLE_DEBOUNCE_MS = 300;
const CONTRIBUTE_DRAFT_KEY = 'repertoire:contribute-draft';
const TITLE_MIN_LENGTH = 2;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;

type ContributeApiError = { status: number; body?: { detail?: string; retry_after?: number } };

function asApiError(error: unknown): ContributeApiError | null {
  if (error instanceof ApiError) {
    return { status: error.status, body: error.body as ContributeApiError['body'] };
  }
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const candidate = error as ContributeApiError;
    if (typeof candidate.status === 'number') return candidate;
  }
  return null;
}

function humaniseRetryAfter(seconds: number): string {
  if (seconds >= SECONDS_PER_HOUR) {
    const hours = Math.round(seconds / SECONDS_PER_HOUR);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }
  if (seconds >= SECONDS_PER_MINUTE) {
    const minutes = Math.round(seconds / SECONDS_PER_MINUTE);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }
  return `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
}

type SubmitBanner =
  | { kind: 'validation'; detail: string }
  | { kind: 'rate-limited'; retryAfter: string }
  | { kind: 'generic' };

function bannerFromError(error: unknown): SubmitBanner {
  const apiError = asApiError(error);
  if (apiError?.status === 400) {
    return { kind: 'validation', detail: apiError.body?.detail ?? '' };
  }
  if (apiError?.status === 429) {
    const retryAfter = apiError.body?.retry_after ?? 0;
    return { kind: 'rate-limited', retryAfter: humaniseRetryAfter(retryAfter) };
  }
  return { kind: 'generic' };
}

const ALLOWED_RETURN_TO = '/repertoire/add' as const;
type AllowedReturnTo = typeof ALLOWED_RETURN_TO;

type ContributeSearch = {
  title?: string;
  returnTo?: AllowedReturnTo;
};

export const Route = createFileRoute('/library_/contribute')({
  component: ContributePiecePage,
  validateSearch: (search: Record<string, unknown>): ContributeSearch => ({
    title: typeof search.title === 'string' ? search.title : undefined,
    returnTo: search.returnTo === ALLOWED_RETURN_TO ? ALLOWED_RETURN_TO : undefined,
  }),
});

function ContributePiecePage(): ReactElement {
  const { title: initialTitle, returnTo } = Route.useSearch();
  const [state, dispatch] = useLocalStorageReducer(
    CONTRIBUTE_DRAFT_KEY,
    contributeFormReducer,
    { title: initialTitle },
    initialContributeFormState,
    contributeFormStorageReplacer,
  );

  // ?title= (the F4 hand-off) wins over a stale stored title, but only on
  // first mount — the user is free to edit it back afterwards.
  const titleOverrideApplied = useRef(false);
  useEffect(() => {
    if (titleOverrideApplied.current) return;
    titleOverrideApplied.current = true;
    if (initialTitle && initialTitle !== state.title) {
      dispatch({ type: 'SET_TITLE', title: initialTitle });
    }
    // We intentionally only run this once on mount — re-running on title edits
    // would clobber the user's input with the URL param.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const keysQuery = useKeys();
  const keys = keysQuery.data?.content ?? [];

  const debouncedTitle = useDebouncedValue(state.title, TITLE_DEBOUNCE_MS);
  const duplicatesQuery = usePieceDuplicates(debouncedTitle);
  const duplicates = duplicatesQuery.data ?? [];

  const [acknowledgedTitle, setAcknowledgedTitle] = useState<string | null>(null);
  // duplicatesAcknowledged is derived: it stays true only while debouncedTitle
  // matches the title we last dismissed for. A title edit naturally re-arms.
  const duplicatesAcknowledged = acknowledgedTitle === debouncedTitle;
  const showDuplicatePanel = duplicates.length > 0 && !duplicatesAcknowledged;

  const identifyHeadingId = useId();
  const formHeadingId = useId();
  const composerHeadingId = useId();
  const settingsHeadingId = useId();
  const sourcesHeadingId = useId();

  const navigate = useNavigate();
  const contribute = useContributePiece();
  const uploadResource = useUploadResourceFile();
  const [banner, setBanner] = useState<SubmitBanner | null>(null);

  const titleReady = state.title.trim().length >= TITLE_MIN_LENGTH;
  const formReady = state.forms.length >= 1;
  const settingReady = state.settings.some((s) => s.keyId !== null);
  const isRateLimited = banner?.kind === 'rate-limited';
  const submitReady =
    titleReady &&
    formReady &&
    settingReady &&
    !isRateLimited &&
    (duplicates.length === 0 || duplicatesAcknowledged);

  const handleSubmit = async (): Promise<void> => {
    setBanner(null);
    const payload = buildContributePayload(state);
    try {
      const result = await contribute.mutateAsync(payload);
      await uploadPendingFiles(state.settings, result.arrangements);
      window.localStorage.removeItem(CONTRIBUTE_DRAFT_KEY);
      if (returnTo === ALLOWED_RETURN_TO) {
        void navigate({ to: '/repertoire/add', search: { pieceId: result.pieceId } });
      } else {
        void navigate({ to: '/library/$pieceId', params: { pieceId: result.pieceId } });
      }
    } catch (error) {
      setBanner(bannerFromError(error));
    }
  };

  async function uploadPendingFiles(
    settings: typeof state.settings,
    arrangements: { arrangementId: string }[],
  ): Promise<void> {
    for (let i = 0; i < settings.length; i += 1) {
      const setting = settings[i];
      const arrangement = arrangements[i];
      if (arrangement === undefined) continue;
      await uploadIfPresent(setting.pendingFile, arrangement.arrangementId, SHEET_MUSIC_RESOURCE_TYPE);
      await uploadIfPresent(setting.pendingAudioFile, arrangement.arrangementId, AUDIO_RECORDING_RESOURCE_TYPE);
    }
  }

  async function uploadIfPresent(
    file: File | null,
    arrangementId: string,
    resourceType: string,
  ): Promise<void> {
    if (!(file instanceof File)) return;
    try {
      await uploadResource.mutateAsync({
        arrangementId,
        file,
        resourceType,
        title: file.name,
      });
    } catch {
      // Best-effort: a failed upload must not block navigation to the
      // newly-created piece. The user can re-upload from the arrangement
      // page using the existing affordance.
    }
  }

  return (
    <div className="mx-auto grid max-w-5xl grid-cols-1 gap-10 px-6 py-10 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--ink)]">Contribute a piece</h1>

        <div className="mt-8 flex flex-col gap-10">
          <section aria-labelledby={identifyHeadingId} className="flex flex-col gap-4">
            <h2 id={identifyHeadingId} className="text-lg font-semibold text-[var(--ink)]">
              Identify
            </h2>
            <ContributeIdentifySection
              title={state.title}
              aka={state.aka}
              onTitleChange={(title) => dispatch({ type: 'SET_TITLE', title })}
              onAddAka={(value) => dispatch({ type: 'ADD_AKA', value })}
              onRemoveAka={(index) => dispatch({ type: 'REMOVE_AKA', index })}
            />
            {showDuplicatePanel && (
              <DuplicateMatchPanel
                matches={duplicates}
                onDismiss={() => setAcknowledgedTitle(debouncedTitle)}
              />
            )}
          </section>

          <section aria-labelledby={formHeadingId} className="flex flex-col gap-4">
            <h2 id={formHeadingId} className="text-lg font-semibold text-[var(--ink)]">
              Form
            </h2>
            <ContributeFormSection
              selected={state.forms}
              onToggle={(form) => dispatch({ type: 'TOGGLE_FORM', form })}
            />
          </section>

          <section aria-labelledby={composerHeadingId} className="flex flex-col gap-4">
            <h2 id={composerHeadingId} className="text-lg font-semibold text-[var(--ink)]">
              Composer & origin
            </h2>
            <ContributeComposerSection
              composerMode={state.composerMode}
              composerName={state.composerName}
              composerYear={state.composerYear}
              region={state.region}
              summary={state.summary}
              onModeChange={(mode) => dispatch({ type: 'SET_COMPOSER_MODE', mode })}
              onNameChange={(name) => dispatch({ type: 'SET_COMPOSER_NAME', name })}
              onYearChange={(year) => dispatch({ type: 'SET_COMPOSER_YEAR', year })}
              onRegionChange={(region) => dispatch({ type: 'SET_REGION', region })}
              onSummaryChange={(summary) => dispatch({ type: 'SET_SUMMARY', summary })}
            />
          </section>

          <section aria-labelledby={settingsHeadingId} className="flex flex-col gap-4">
            <h2 id={settingsHeadingId} className="text-lg font-semibold text-[var(--ink)]">
              Settings
            </h2>
            <ContributeSettingsSection
              settings={state.settings}
              keys={keys}
              onAddSetting={() => dispatch({ type: 'ADD_SETTING' })}
              onRemoveSetting={(draftId) => dispatch({ type: 'REMOVE_SETTING', draftId })}
              onUpdateSetting={(draftId, patch) =>
                dispatch({ type: 'UPDATE_SETTING', draftId, patch })
              }
              onMarkPrimary={(draftId) => dispatch({ type: 'MARK_SETTING_PRIMARY', draftId })}
              onSelectFile={(draftId, file) =>
                dispatch({ type: 'SET_SETTING_FILE', draftId, file })
              }
              onSelectAudioFile={(draftId, file) =>
                dispatch({ type: 'SET_SETTING_AUDIO_FILE', draftId, file })
              }
            />
          </section>

          <section aria-labelledby={sourcesHeadingId} className="flex flex-col gap-4">
            <h2 id={sourcesHeadingId} className="text-lg font-semibold text-[var(--ink)]">
              Sources
            </h2>
            <ContributeSourcesSection
              sources={state.sources}
              onAddSource={(kind) => dispatch({ type: 'ADD_SOURCE', kind })}
              onRemoveSource={(draftId) => dispatch({ type: 'REMOVE_SOURCE', draftId })}
              onUpdateSource={(draftId, patch) =>
                dispatch({ type: 'UPDATE_SOURCE', draftId, patch })
              }
            />
          </section>

          {banner !== null && <SubmitBannerView banner={banner} />}

          <div className="mt-4 flex items-center justify-between border-t border-[var(--rule)] pt-6">
            {returnTo === ALLOWED_RETURN_TO ? (
              <Link
                to="/repertoire/add"
                className="text-sm font-medium text-[var(--ink-2)] hover:text-[var(--ink)]"
              >
                Cancel
              </Link>
            ) : (
              <Link
                to="/library"
                className="text-sm font-medium text-[var(--ink-2)] hover:text-[var(--ink)]"
              >
                Cancel
              </Link>
            )}
            <Button
              variant="primary"
              disabled={!submitReady || contribute.isPending}
              aria-busy={contribute.isPending || undefined}
              onClick={() => void handleSubmit()}
            >
              Submit
            </Button>
          </div>
        </div>
      </div>

      <ContributeLivePreview
        state={state}
        duplicateMatchCount={duplicates.length}
        duplicatesAcknowledged={duplicatesAcknowledged}
      />
    </div>
  );
}

function SubmitBannerView({ banner }: { banner: SubmitBanner }): ReactElement {
  if (banner.kind === 'validation') {
    return (
      <div
        role="alert"
        className="rounded-[var(--r-md)] border border-[var(--err)] bg-[var(--err-wash)] px-4 py-3 text-sm text-[var(--err)]"
      >
        <p className="font-semibold">Please fix the highlighted issues before submitting.</p>
        {banner.detail && <p className="mt-1 text-xs">{banner.detail}</p>}
      </div>
    );
  }
  if (banner.kind === 'rate-limited') {
    return (
      <div
        role="alert"
        className="rounded-[var(--r-md)] border border-[var(--err)] bg-[var(--err-wash)] px-4 py-3 text-sm text-[var(--err)]"
      >
        <p className="font-semibold">Daily submission limit reached.</p>
        <p className="mt-1 text-xs">Try again in {banner.retryAfter}.</p>
      </div>
    );
  }
  return (
    <div
      role="alert"
      className="rounded-[var(--r-md)] border border-[var(--err)] bg-[var(--err-wash)] px-4 py-3 text-sm text-[var(--err)]"
    >
      <p className="font-semibold">Couldn&apos;t submit — try again.</p>
    </div>
  );
}
